
import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Box, Typography
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, CheckCircle as ActivateIcon, Search as SearchIcon, Info as Info } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import CopyrightIcon from '@mui/icons-material/Copyright';
import { Tooltip } from '@mui/material';
import '../../../stylesheet.css';
import { useLocation } from 'react-router-dom';
import ResponsiveTable from '../../ResponsiveTable';

function PurchaseIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, token, logout, userId } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const [isLoading, setIsLoading] = useState(false);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [fileData, setFileData] = useState([]);
    const [isFocused, setIsFocused] = useState(false);
    const branchId = 0;
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {

        // Check if reload is needed based on the flag set in localStorage
        const shouldReload = localStorage.getItem('shouldReloadCreatePage') === 'true';

        if (shouldReload) {
            localStorage.removeItem('shouldReloadCreatePage'); // Remove the flag after use
            window.location.reload(); // Reload the page once
        }

        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('PurchaseApi', `/PurchaseDetails/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }
    };

    useEffect(() => {
        const shouldReload = localStorage.getItem('shouldReload') === 'true';

        // Only reload data if navigated from PurchaseOrderCreate and flag is set
        if (location.state?.from === '/PurchaseCreate' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();

        }
        else if (location.state?.from === '/PurchaseEdit' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }

        else if (location.state?.from === '/PurchaseDetails' && shouldReload) {
            localStorage.removeItem('shouldReload'); // Clear the flag after use
            window.location.reload();
        }
        else {
            // Populate data without reloading for other cases
            setFromDate(getFormattedStartDate());
            setToDate(getFormattedCurrentDate());
            // handleClickOpen(false);
        }
    }, [location]);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const handleDelete = async () => {
        deleteData('PurchaseApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchtableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('PurchaseApi', `${itemId}`, token)
            .then(() => {
                fetchtableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    // Define table columns
    const columns = [
        { 
            field: 'BillDate', 
            header: 'Bill Date',
            render: (row) => formatDate(row.BillDate)
        },
        { field: 'BillNumber', header: 'Bill No' },
        { field: 'SupplierName', header: 'Supplier' },
        { 
            field: 'Amount', 
            header: 'Amount',
            render: (row) => row.Amount?.toFixed(2) || '0.00'
        },
        { 
            field: 'CGSTAmount', 
            header: 'CGST',
            render: (row) => row.CGSTAmount?.toFixed(2) || '0.00'
        },
        { 
            field: 'SGSTAmount', 
            header: 'SGST',
            render: (row) => row.SGSTAmount?.toFixed(2) || '0.00'
        },
        { 
            field: 'IGSTAmount', 
            header: 'IGST',
            render: (row) => row.IGSTAmount?.toFixed(2) || '0.00'
        },
        { 
            field: 'TotalAmount', 
            header: 'Total',
            render: (row) => row.TotalAmount?.toFixed(2) || '0.00'
        },
        { field: 'TotalQuantity', header: 'Qty' },
    ];

    // Define action buttons for each row
    const renderActions = (item) => (
        <>
            <Tooltip title="Purchase Details" arrow>
                <IconButton 
                    size="small"
                    component={Link} 
                    to={`/PurchaseDetails/${item.Id}`} 
                    color="warning"
                >
                    <Info style={{ fontSize: '19px' }} />
                </IconButton>
            </Tooltip>

            {hasEditMasterPermission && item.IsActive && (
                <Tooltip title="Edit Purchase" arrow>
                    <IconButton 
                        size="small"
                        component={Link} 
                        to={`/PurchaseEdit/${item.Id}`} 
                        color="primary"
                    >
                        <EditIcon style={{ fontSize: '19px' }} />
                    </IconButton>
                </Tooltip>
            )}

            {hasDeleteMasterPermission && item.IsActive && (
                <Tooltip title="Delete Purchase" arrow>
                    <IconButton 
                        size="small"
                        onClick={() => openConfirmDialog(item.Id)} 
                        color="error"
                    >
                        <DeleteIcon style={{ fontSize: '19px' }} />
                    </IconButton>
                </Tooltip>
            )}

            {!item.IsActive && (
                <Tooltip title="Activate Purchase" arrow>
                    <IconButton 
                        size="small"
                        onClick={() => handleActivate(item.Id)} 
                        color="success"
                    >
                        <ActivateIcon style={{ fontSize: '19px' }} />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', md: '98%' },
                mx: 'auto',
                mt: { xs: 1, md: -3 },
                px: { xs: 0, sm: 0, md: 0 },
            }}
        >
            {/* Filter Bar */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: { xs: 1.5, sm: 1.5 },
                    p: { xs: 1.5, sm: 2 },
                    mx: { xs: 2, sm: 2, md: 0 },
                    borderRadius: '12px',
                    background: '#f9fafb',
                    mb: { xs: 1.5, sm: 1.5 },
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                }}
            >
                <h6
                    className="uppercase tracking-wide font-serif text-black text-gray-700"
                    style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        margin: 0,
                        flex: { xs: 'none', sm: '1' }
                    }}
                >
                    Purchase
                </h6>

                {/* Search - Full width on mobile */}
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <input
                        type="text"
                        placeholder="🔍 Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: '100%',
                            height: '32px',
                            border: '1px solid #ddd',
                            borderRadius: '20px',
                            padding: '0 14px',
                            fontSize: '12px',
                            transition: 'all 0.3s ease',
                            outline: 'none',
                        }}
                    />
                </Box>

                {/* Date Filters - Stack on mobile */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' }
                    }}
                >
                    <input
                        type="date"
                        value={fromDate}
                        onChange={handleFromDateChange}
                        style={{
                            flex: 1,
                            height: '32px',
                            fontSize: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            padding: '0 6px',
                            fontFamily: 'Arial, sans-serif',
                        }}
                    />

                    <input
                        type="date"
                        value={toDate}
                        onChange={handleToDateChange}
                        style={{
                            flex: 1,
                            height: '32px',
                            fontSize: '12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            padding: '0 6px',
                            fontFamily: 'Arial, sans-serif',
                        }}
                    />
                </Box>

                {/* Action Buttons - Full width on mobile */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        gap: 1,
                        width: { xs: '100%', sm: 'auto' }
                    }}
                >
                    <button
                        onClick={fetchtableData}
                        style={{
                            flex: { xs: 1, sm: 'none' },
                            background: '#1090a3',
                            color: 'white',
                            fontSize: '12px',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '6px 14px',
                            cursor: 'pointer',
                            transition: '0.2s',
                            height: '32px',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#0b6d7a')}
                        onMouseOut={(e) => (e.currentTarget.style.background = '#1090a3')}
                    >
                        Refresh
                    </button>
                    
                    {hasCreateMasterPermission && (
                        <Tooltip title="Create New Purchase" arrow>
                            <Button
                                component={Link}
                                to="/PurchaseCreate"
                                sx={{
                                    flex: { xs: 1, sm: 'none' },
                                    background: '#3F4F44',
                                    color: 'white',
                                    fontSize: '12px',
                                    borderRadius: '20px',
                                    padding: '6px 14px',
                                    textTransform: 'none',
                                    fontFamily: 'inherit',
                                    height: '32px',
                                    minWidth: 'auto',
                                    '&:hover': {
                                        background: '#2f3f34',
                                    }
                                }}
                            >
                                Create
                            </Button>
                        </Tooltip>
                    )}
                </Box>
            </Box>

            {/* Responsive Table */}
            <Box sx={{ mb: 5 }}>
                <ResponsiveTable
                    columns={columns}
                    mobileColumns={['SupplierName', 'TotalAmount']}
                    mobileHeader={(row, index) => (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: 0.5,
                            width: '100%',
                            color: 'white'
                        }}>
                            <Typography sx={{ 
                                fontSize: '14px', 
                                fontWeight: 'bold',
                                color: 'white'
                            }}>
                                Bill No: {row.BillNumber}
                            </Typography>
                            <Typography sx={{ 
                                fontSize: '12px',
                                color: 'rgba(255, 255, 255, 0.9)'
                            }}>
                                {formatDate(row.BillDate)}
                            </Typography>
                            {row.IsActive === false && (
                                <Box
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.3,
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        alignSelf: 'flex-start',
                                        mt: 0.5,
                                    }}
                                >
                                    Inactive
                                </Box>
                            )}
                        </Box>
                    )}
                    data={filteredData}
                    actions={renderActions}
                />
            </Box>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Delete Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <button
                        onClick={closeConfirmDialog}
                        style={{
                            backgroundColor: '#dc3545',
                            color: '#ffffff',
                            border: '1px solid #dc3545',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                            marginRight: '8px',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        style={{
                            backgroundColor: '#28a745',
                            color: '#ffffff',
                            border: '1px solid #28a745',
                            borderRadius: '4px',
                            padding: '8px 16px',
                            cursor: 'pointer',
                        }}
                    >
                        Confirm
                    </button>
                </DialogActions>
            </Dialog>

            {/* Watermark */}
            <div style={{
                position: 'fixed',
                bottom: '12px',
                right: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.3rem 0.65rem',
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '14px',
                boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06)',
                fontFamily: 'serif',
                fontWeight: 'bold',
                color: '#2c3e50',
                fontSize: '0.7rem',
                backdropFilter: 'blur(10px)'
            }}>
                <CopyrightIcon />
                <b>EFFTABS SOLUTIONS LLP</b>
            </div>
        </Box>
    );
}

export default PurchaseIndex;



